import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import card200 from "../assets/giftcards/gift-card-200.png";
import card500 from "../assets/giftcards/gift-card-500.png";
import card1000 from "../assets/giftcards/gift-card-1000.png";

/* Sahibin kart dizaynları (2026-09-16) — seçim elə kartın özüdür. */
const CARD_ART = { 20000: card200, 50000: card500, 100000: card1000 };
import { useCart } from "../lib/cart";
import { useI18n } from "../lib/i18n";
import { formatAZN } from "../lib/format";
import { apiErrorMessage } from "../lib/apiErrors";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoMeta from "../components/storefront/SeoMeta";
import { SmallCaps, PillCTA } from "../components/atelier/primitives";
import { analytics } from "../lib/analytics";

/**
 * Buying a gift card.
 *
 * Two steps, then the ordinary checkout — not the four the reference uses. Its
 * third and fourth steps are the delivery address and the amount to pay, and
 * this shop already collects both, along with the phone confirmation and cash
 * on delivery. Repeating them here would be a second checkout to maintain.
 *
 * The amounts are the catalogue's own variants rather than a free-entry box, so
 * the card is priced, ordered, invoiced and refunded on exactly the path every
 * other purchase takes.
 */
export default function GiftCard() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { addItem } = useCart();
  useDocumentTitle(t("gift.doc_title", "Hədiyyə kartı"));

  const [options, setOptions] = useState(null);
  const [variantId, setVariantId] = useState("");
  /* A shopper-typed amount, in whole manats. Choosing it deselects the ladder. */
  const [customAmount, setCustomAmount] = useState("");
  const [customActive, setCustomActive] = useState(false);
  const [recipient, setRecipient] = useState({
    recipient_name: "", recipient_email: "", recipient_phone: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/gift-cards/options")
      .then((r) => {
        setOptions(r.data);
        const list = r.data?.options || [];
        // The middle of the ladder rather than the cheapest: a preselected
        // minimum reads as a suggestion to spend the least.
        if (list.length) setVariantId(list[Math.floor(list.length / 2)].variant_id);
      })
      .catch(() => setOptions({ available: false, options: [] }));
  }, []);

  const chosen = useMemo(
    () => (options?.options || []).find((o) => o.variant_id === variantId) || null,
    [options, variantId],
  );
  const minMinor = options?.min_amount_minor || 5000;
  const maxMinor = options?.max_amount_minor || 1000000;
  const customMinor = Math.round(Number(customAmount || 0)) * 100;
  const customValid = customActive && customMinor >= minMinor && customMinor <= maxMinor;
  const payableMinor = customActive ? (customValid ? customMinor : null) : (chosen?.amount_minor ?? null);

  const set = (key, value) => setRecipient((current) => ({ ...current, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (customActive ? !customValid : !chosen) return;
    setError("");
    setBusy(true);
    try {
      let targetVariant = chosen?.variant_id;
      if (customActive) {
        /* The server mints (or finds) the variant for this amount, validating
           the admin-set floor again — the input's own check is just manners. */
        const r = await api.post("/gift-cards/custom-variant", { amount_minor: customMinor });
        targetVariant = r.data.variant_id;
      }
      await addItem(targetVariant, 1, {
        recipient_name: recipient.recipient_name.trim() || undefined,
        recipient_email: recipient.recipient_email.trim() || undefined,
        recipient_phone: recipient.recipient_phone.trim() || undefined,
        message: recipient.message.trim() || undefined,
      });
      analytics.addToCart({ handle: "hediyye-karti", title: "Hədiyyə kartı", price: payableMinor });
      navigate("/cart");
    } catch (err) {
      setError(apiErrorMessage(err, t));
      setBusy(false);
    }
  };

  const validYears = Math.round((options?.valid_for_days || 730) / 365);

  return (
    <div className="min-h-screen bg-white text-graphite" data-testid="gift-card-page">
      <SeoMeta
        title={t("gift.seo_title", "Hədiyyə kartı — Atelier Di Falco")}
        description={t("gift.seo_desc", "Atelier Di Falco hədiyyə kartı: məbləği seçin, kod alıcıya e-poçtla gedir, fiziki kart ünvana çatdırılır.")}
        locale={locale}
        canonicalPath="/gift-card"
      />
      <Header />
      <main id="main" className="mx-auto max-w-[900px] px-5 pb-20 pt-10 sm:px-8">
        <SmallCaps>{t("gift.eyebrow", "Hədiyyə")}</SmallCaps>
        <h1 className="mt-4 font-serif text-[clamp(32px,4.4vw,48px)] leading-tight">
          {t("gift.title", "Atelier Di Falco hədiyyə kartı")}
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-7 text-graphite/75">
          {t("gift.intro", "Məbləği seçin. Kod alıcıya e-poçtla göndərilir, fiziki kart isə sifariş ünvanına çatdırılır. Kartı bir neçə alışa bölmək olar — qalıq kartda saxlanılır.")}
        </p>

        {options && !options.available && (
          <p className="mt-10 border border-graphite/15 p-6 text-sm text-graphite/75" data-testid="gift-unavailable">
            {t("gift.unavailable", "Hədiyyə kartları hazırda satışda deyil.")}
          </p>
        )}

        {options?.available && (
          <form onSubmit={submit} className="mt-10" data-testid="gift-form">
            <section>
              <h2 className="text-[11px] uppercase tracking-widest text-graphite/60">
                {t("gift.step1", "1 · Məbləğ")}
              </h2>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="gift-amounts">
                {options.options.map((option) => {
                  const selected = option.variant_id === variantId;
                  return (
                    <button
                      key={option.variant_id}
                      type="button"
                      onClick={() => { setVariantId(option.variant_id); setCustomActive(false); }}
                      aria-pressed={selected}
                      className={`min-h-[64px] border px-4 py-3 font-serif text-xl transition ${
                        selected
                          ? "border-graphite bg-graphite text-white"
                          : "border-graphite/20 text-graphite hover:border-espresso"
                      }`}
                      data-testid={`gift-amount-${option.amount_minor}`}
                    >
                      {CARD_ART[option.amount_minor] ? (
                        <span className="block">
                          <img src={CARD_ART[option.amount_minor]} alt={option.label} className="block w-full" />
                          <span className="mt-2 block text-center text-sm">{formatAZN(option.amount_minor)}</span>
                        </span>
                      ) : (
                        formatAZN(option.amount_minor)
                      )}
                    </button>
                  );
                })}
              </div>
              {options?.custom_allowed && (
                <div className="mt-4" data-testid="gift-custom">
                  <label
                    className={`flex min-h-[64px] cursor-pointer items-center gap-4 border px-4 py-3 transition ${customActive ? "border-graphite" : "border-graphite/20 hover:border-espresso"}`}
                  >
                    <input
                      type="radio"
                      checked={customActive}
                      onChange={() => setCustomActive(true)}
                      className="h-4 w-4 accent-graphite"
                      aria-label={t("gift.custom", "Öz məbləğiniz")}
                    />
                    <span className="text-sm text-graphite/75">{t("gift.custom", "Öz məbləğiniz")}</span>
                    <div className="ml-auto flex items-center gap-2">
                      <input
                        type="number"
                        min={Math.ceil(minMinor / 100)}
                        max={Math.floor(maxMinor / 100)}
                        step={1}
                        value={customAmount}
                        onFocus={() => setCustomActive(true)}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder={String(Math.ceil(minMinor / 100))}
                        className="h-11 w-28 border border-graphite/20 bg-white px-3 text-right font-serif text-lg outline-none placeholder:text-graphite/30 focus:border-graphite"
                        data-testid="gift-custom-amount"
                      />
                      <span className="font-serif text-lg">₼</span>
                    </div>
                  </label>
                  <p className="mt-2 text-xs text-graphite/55">
                    {t("gift.custom_hint", "Minimum {min} ₼, tam manatla.", { min: Math.ceil(minMinor / 100) })}
                    {customActive && customAmount && !customValid && (
                      <span className="ml-2 text-[#8a3b2e]" data-testid="gift-custom-error">
                        {customMinor < minMinor
                          ? t("gift.custom_min_error", "Minimum {min} ₼-dir.", { min: Math.ceil(minMinor / 100) })
                          : t("gift.custom_max_error", "Maksimum {max} ₼-dir.", { max: Math.floor(maxMinor / 100) })}
                      </span>
                    )}
                  </p>
                </div>
              )}
            </section>

            <section className="mt-10">
              <h2 className="text-[11px] uppercase tracking-widest text-graphite/60">
                {t("gift.step2", "2 · Kimə")}
              </h2>
              <p className="mt-2 text-sm text-graphite/65">
                {t("gift.step2_hint", "İstəyə bağlıdır. Boş buraxsanız kod sizə gələcək və özünüz çatdıra bilərsiniz.")}
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field
                  label={t("gift.recipient_name", "Alıcının adı")}
                  placeholder={t("gift.recipient_name", "Alıcının adı")}
                  value={recipient.recipient_name}
                  onChange={(v) => set("recipient_name", v)}
                  testId="gift-recipient-name"
                />
                <Field
                  label={t("gift.recipient_email", "Alıcının e-poçtu")}
                  placeholder="email@example.com"
                  type="email"
                  value={recipient.recipient_email}
                  onChange={(v) => set("recipient_email", v)}
                  testId="gift-recipient-email"
                />
              </div>
              <div className="mt-4">
                <Field
                  label={t("gift.message", "Qısa mesaj")}
                  placeholder={t("gift.message_ph", "Təbrik mesajınız…")}
                  value={recipient.message}
                  onChange={(v) => set("message", v)}
                  maxLength={500}
                  testId="gift-message"
                />
              </div>
            </section>

            {error && (
              <p className="mt-6 border border-burgundy/40 bg-burgundy/5 p-4 text-sm text-burgundy" role="alert">
                {error}
              </p>
            )}

            <p className="mt-6 border border-graphite/15 px-4 py-3 text-sm leading-6 text-graphite/75" data-testid="gift-destination">
              {recipient.recipient_email.trim()
                ? t("gift.goes_to_recipient", "Kod {email} ünvanına göndəriləcək.", { email: recipient.recipient_email.trim() })
                : t("gift.goes_to_buyer", "Alıcının e-poçtunu yazmasanız, kod sizə göndəriləcək və özünüz çatdıra bilərsiniz. Səbətdə istənilən vaxt dəyişə bilərsiniz.")}
            </p>

            <div className="mt-6 flex flex-col gap-4 border-t border-graphite/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-graphite/60">
                  {t("gift.total", "Ödəniləcək")}
                </div>
                <div className="font-serif text-3xl" data-testid="gift-total">
                  {payableMinor != null ? formatAZN(payableMinor) : "—"}
                </div>
              </div>
              <PillCTA
                type="submit"
                variant="primary"
                disabled={busy || payableMinor == null}
                className="min-h-[52px] px-10"
                data-testid="gift-submit"
              >
                {busy ? t("gift.adding", "Əlavə olunur…") : t("gift.continue", "Səbətə əlavə et")}
              </PillCTA>
            </div>

            <p className="mt-6 text-xs leading-6 text-graphite/60">
              {t("gift.terms", `Kart ${validYears} il etibarlıdır. Ödəniş təsdiqləndikdən sonra kod göndərilir. Hədiyyə kartı ilə başqa hədiyyə kartı almaq olmaz.`)}
            </p>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", maxLength, testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-graphite">{label}</span>
      <input
        type={type}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 min-h-[48px] w-full border border-graphite/20 bg-white px-3 text-sm placeholder:text-graphite/35 focus:border-espresso focus:outline-none"
        data-testid={testId}
      />
    </label>
  );
}
